"use client";

import { useIsDesktop } from "@/hooks/useIsDesktop";

const PJK = "'Plus Jakarta Sans', var(--font-plus-jakarta), sans-serif";
const URB = "var(--font-urbanist), sans-serif";

export type BudgetMode = "task" | "hourly";

const HOURLY_RATE = 15;
const HOURLY_MIN_HOURS = 2;
const HOURLY_MAX_HOURS = 3;

interface BudgetCardProps {
  recommended: number;
  tiers: number[];
  selected: number;
  onTierSelect?: (tier: number) => void;
  onConfirm?: () => void;
  mode?: BudgetMode;
  onModeChange?: (mode: BudgetMode) => void;
}

export default function BudgetCard({
  recommended,
  tiers,
  selected,
  onTierSelect,
  onConfirm,
  mode = "task",
  onModeChange,
}: BudgetCardProps) {
  const hourlyMin = HOURLY_RATE * HOURLY_MIN_HOURS;
  const hourlyMax = HOURLY_RATE * HOURLY_MAX_HOURS;
  const isDesktop = useIsDesktop();
  const priceFontSize = isDesktop ? 40 : 48;

  return (
    <div
      className="desktop-glass-budget-card"
      style={{
        background: "#1E1E1E",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 16,
        boxShadow: "0px 25px 50px -12px rgba(0,0,0,0.4)",
        padding: 1,
        overflow: "clip",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        alignItems: "center",
        width: 720,
      }}
    >
      {/* Mode toggle — web only; Figma 88:8544 omits it on desktop. */}
      {!isDesktop && (
        <div
          style={{
            width: "100%",
            paddingTop: 20,
            paddingLeft: 24,
            paddingRight: 24,
            display: "flex",
            gap: 8,
          }}
        >
          <ModePill
            label="Per task"
            active={mode === "task"}
            onClick={() => onModeChange?.("task")}
          />
          <ModePill
            label={`Hourly ($${HOURLY_RATE}/hr)`}
            active={mode === "hourly"}
            onClick={() => onModeChange?.("hourly")}
          />
        </div>
      )}

      {/* Header */}
      <div style={{ width: "100%", paddingTop: 16, paddingBottom: 16, paddingLeft: 24, paddingRight: 24 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4.5, alignItems: "flex-start" }}>
          <p
            style={{
              fontFamily: PJK,
              fontWeight: 700,
              fontSize: 18,
              lineHeight: "28px",
              color: "#F2F0ED",
              margin: 0,
            }}
          >
            {mode === "hourly" ? "Hourly Rate" : "Recommended Budget"}
          </p>
          {mode === "task" ? (
            <div style={{ display: "flex", gap: 7.99, alignItems: "flex-end" }}>
              <span
                key={`price-${selected}`}
                className="app-anim-price-pulse"
                style={{
                  fontFamily: URB,
                  fontWeight: 900,
                  fontSize: priceFontSize,
                  lineHeight: `${priceFontSize}px`,
                  letterSpacing: "-1.2px",
                  color: "#F6AE2D",
                  textAlign: "center",
                  display: "inline-block",
                }}
              >
                ${recommended}
              </span>
              <span
                style={{
                  fontFamily: URB,
                  fontWeight: 500,
                  fontSize: 16,
                  lineHeight: "24px",
                  color: "rgba(242,240,237,0.4)",
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                / per task
              </span>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
              <div style={{ display: "flex", gap: 7.99, alignItems: "flex-end" }}>
                <span
                  style={{
                    fontFamily: URB,
                    fontWeight: 900,
                    fontSize: priceFontSize,
                    lineHeight: `${priceFontSize}px`,
                    letterSpacing: "-1.2px",
                    color: "#F6AE2D",
                    textAlign: "center",
                    display: "inline-block",
                  }}
                >
                  ${HOURLY_RATE}
                </span>
                <span
                  style={{
                    fontFamily: URB,
                    fontWeight: 500,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "rgba(242,240,237,0.4)",
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  / hr
                </span>
              </div>
              <span
                style={{
                  fontFamily: URB,
                  fontWeight: 500,
                  fontSize: 14,
                  lineHeight: "20px",
                  color: "#CDC2D8",
                }}
              >
                Estimated: {HOURLY_MIN_HOURS}–{HOURLY_MAX_HOURS} hours
              </span>
              <span
                style={{
                  fontFamily: URB,
                  fontWeight: 500,
                  fontSize: 13,
                  lineHeight: "18px",
                  color: "rgba(242,240,237,0.5)",
                }}
              >
                Total estimate: ${hourlyMin}–${hourlyMax}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Budget Tiers — only in per-task mode */}
      {mode === "task" && (
        <div
          style={{
            width: "100%",
            paddingLeft: 24,
            paddingRight: 16,
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 12,
            boxSizing: "border-box",
          }}
        >
          {tiers.map((tier) => {
            const isSelected = tier === selected;
            return (
              <button
                key={tier}
                onClick={() => onTierSelect?.(tier)}
                className={isSelected ? "app-tier-selected" : "app-tier"}
                style={{
                  height: isDesktop ? 44 : 64,
                  borderRadius: 999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: isSelected ? "12px 29px" : "12px 24px",
                  background: isSelected ? "#8635F6" : "#2D2C2C",
                  boxShadow: isSelected
                    ? "0px 0px 20px 0px rgba(134,53,246,0.3)"
                    : "none",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: URB,
                  fontWeight: isSelected ? 700 : 600,
                  fontSize: isDesktop ? 14 : 28,
                  lineHeight: isDesktop ? "20px" : "36px",
                  color: isSelected ? "#ffffff" : "rgba(242,240,237,0.5)",
                  transition: "all 200ms ease-out",
                }}
              >
                ${tier}
              </button>
            );
          })}
        </div>
      )}

      {/* Textarea — per-task only (open custom amount) */}
      {mode === "task" && (
        <div style={{ width: 718, paddingTop: 12, paddingBottom: 72, paddingLeft: 12, paddingRight: 12 }}>
          <textarea
            placeholder="Or propose your own value..."
            rows={1}
            className="app-textarea"
            style={{
              width: "100%",
              minHeight: 44,
              maxHeight: 160,
              padding: "10px 12px",
              background: "transparent",
              border: "none",
              outline: "none",
              resize: "none",
              fontFamily: URB,
              fontWeight: 400,
              fontSize: 16,
              lineHeight: "24px",
              color: "rgba(242,240,237,0.5)",
            }}
          />
        </div>
      )}

      {mode === "hourly" && <div style={{ paddingBottom: 40 }} />}

      {/* Footer */}
      <div
        className="desktop-glass-budget-footer"
        style={{
          background: "#2D2C2C",
          width: "100%",
          paddingLeft: 24,
          paddingRight: 16,
          paddingTop: 16,
          paddingBottom: 16,
          display: "flex",
          alignItems: "stretch",
        }}
      >
        <button
          onClick={onConfirm}
          className="app-btn-primary"
          style={{
            background: "#8635F6",
            borderRadius: 9999,
            flex: 1,
            // Figma 88:8544 specifies h-[40px] on desktop; py-16 inflates
            // it to ~56px. Collapse to py-10 on desktop for a 40px pill.
            paddingTop: isDesktop ? 10 : 16,
            paddingBottom: isDesktop ? 10 : 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            border: "none",
            cursor: "pointer",
            boxShadow:
              "0px 10px 15px -3px rgba(134,53,246,0.2), 0px 4px 6px -4px rgba(134,53,246,0.2)",
            fontFamily: isDesktop ? PJK : URB,
            fontWeight: 700,
            fontSize: isDesktop ? 14 : 16,
            lineHeight: "24px",
            color: "#F2F0ED",
            textAlign: "center",
          }}
        >
          Confirm budget &amp; proceed
          <img
            src="/app-icons/arrow-right.svg"
            alt=""
            style={{ width: 24, height: 24 }}
          />
        </button>
      </div>
    </div>
  );
}

function ModePill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={active ? "app-btn-primary" : "app-btn-secondary"}
      style={{
        padding: "6px 14px",
        borderRadius: 9999,
        background: active ? "#8635F6" : "transparent",
        border: active ? "none" : "1px solid #2E2A33",
        color: active ? "#F6F5F5" : "#CDC2D8",
        fontFamily: PJK,
        fontWeight: 600,
        fontSize: 12,
        cursor: "pointer",
        transition: "all 150ms ease-out",
      }}
    >
      {label}
    </button>
  );
}
